import {
  InvestorDetails,
  InvestorFundDetails,
  Options,
  YES,
} from "../../../../store/types";

export * from "./AdditionalPurchase";
export * from "./NewPurchase";
export * from "./Sip";
export * from "./Switch";
export * from "./STP";
export * from "./SWP";
export * from "./Redemption";

interface FundOption {
  label: string;
  value: string;
  opt: string;
  schemeType: string;
}

export const SIP_TYPE_OPTIONS_NEW = [
  {
    label: "New Folio - SIP Registration with payment",
    value: "NEWP",
  },
  { label: "New Folio - SIP Registration", value: "NEW" },
];

export const SIP_TYPE_OPTIONS_EXISTING = [
  { label: "Existing Folio - SIP Registration", value: "EXISTING" },
  {
    label: "Existing Folio - SIP Registration with payment",
    value: "EXISTINGP",
  },
];

export const SWITCH_TYPE_OPTIONS = [
  { label: "partial", value: "partial" },
  {
    label: "full",
    value: "full",
  },
];

export const SWITCH_MODE_OPTIONS = [
  { label: "amount", value: "amount" },
  { label: "units", value: "units" },
];

export const getStpFundOptions = (
  fundDetails: InvestorDetails["fundDetails"]
): Options => {
  return fundDetails.reduce<Options>((prev, { Fund, FundDesc, sipFlag }) => {
    if (sipFlag !== YES) return prev;

    const index = prev.findIndex((fund) => fund.value === Fund);

    if (index !== -1) return prev;

    const currentOption = {
      label: FundDesc,
      value: Fund,
    };
    return [...prev, currentOption];
  }, []);
};
export const getSipFundOptions = (
  fundDetails: InvestorDetails["fundDetails"]
): Array<FundOption> => {
  return fundDetails.reduce<Array<FundOption>>(
    (prev, { Fund, FundDesc, Opt, SchType, sFlag }) => {
      if (sFlag !== YES) return prev;
      const index = prev.findIndex((fund) => fund.value === Fund);

      if (index !== -1) return prev;

      const currentOption = {
        label: FundDesc,
        value: Fund,
        opt: Opt,
        schemeType: SchType,
      };
      return [...prev, currentOption];
    },
    []
  );
};

export const getFundOptions = (
  fundDetails: InvestorDetails["fundDetails"]
): Array<FundOption> => {
  return fundDetails.reduce<Array<FundOption>>(
    (prev, { sSchFlg, Fund, FundDesc, Opt, SchType }) => {
      if (sSchFlg !== YES) {
        return prev;
      }
      const index = prev.findIndex((fund) => fund.value === Fund);
      if (index === -1) {
        const currentOption = {
          label: FundDesc,
          value: Fund,
          opt: Opt,
          schemeType: SchType,
        };
        return [...prev, currentOption];
      }
      return prev;
    },
    []
  );
};

function folioMap(folio: number): Options[number] {
  return {
    label: folio.toString(),
    value: folio.toString(),
  };
}

export const getFolioOptions = (
  fundDetails: InvestorDetails["fundDetails"],
  fundCode: string
): Options => {
  const folios = new Set(
    fundDetails
      .filter(({ sipFlag, Fund }) => fundCode === Fund && sipFlag === YES)
      .map((s) => s.Acno)
  );
  return Array.from(folios).map(folioMap);
};

export const getSwpFolios = (
  fundDetails: InvestorDetails["fundDetails"],
  fundCode: string
): Options => {
  return Array.from(
    new Set(
      fundDetails.reduce((prev, { sFlag, Acno, Fund }) => {
        if (fundCode === Fund && sFlag === YES) {
          const currentOption = Acno;
          return [...prev, currentOption];
        }
        return prev;
      }, [])
    )
  ).map(folioMap);
};

export const getRedemptionFolios = (
  fundDetails: InvestorDetails["fundDetails"],
  fundCode: string
): Options => {
  return Array.from(
    new Set(
      fundDetails.reduce((prev, { rSchFlg, Acno, Fund }) => {
        if (fundCode === Fund && rSchFlg === YES) {
          const currentOption = Acno;
          return [...prev, currentOption];
        }
        return prev;
      }, [])
    )
  ).map(folioMap);
};

export const getSwitchFolios = (
  fundDetails: InvestorDetails["fundDetails"],
  fundCode: string
): Options => {
  return Array.from(
    new Set(
      fundDetails.reduce((prev, { sSchFlg, Acno, Fund }) => {
        if (fundCode === Fund && sSchFlg === YES) {
          const currentOption = Acno;
          return [...prev, currentOption];
        }
        return prev;
      }, [])
    )
  ).map(folioMap);
};

export const getStpFolios = (
  fundDetails: InvestorDetails["fundDetails"],
  fundCode: string
): Options => {
  return Array.from(
    new Set(
      fundDetails.reduce((prev, { Acno, Fund }) => {
        if (fundCode === Fund) {
          const currentOption = Acno;
          return [...prev, currentOption];
        }
        return prev;
      }, [])
    )
  ).map(folioMap);
};

export const getSwitchOutOptions = (
  fundDetails: InvestorFundDetails[],
  fundCode: string,
  folio: string | number
): {
  scheme: string;
  plan: string;
  option: string;
  label: string;
  isMultiBroker: boolean;
  details: InvestorFundDetails;
  value: string;
}[] => {
  return fundDetails.reduce((prev, curr) => {
    const { sSchFlg, BalUnits, Acno, Sch, Pln, Opt, Fund, multibrokflg } = curr;
    const isValidSchFlag = sSchFlg === YES;
    const isSameFund = Fund === fundCode;
    const isSameFolio = String(folio) === String(Acno);

    const isValidScheme = [
      isValidSchFlag,
      isSameFund,
      isSameFolio,
      BalUnits,
    ].every(Boolean);

    if (isValidScheme) {
      const currentOption = {
        scheme: Sch,
        plan: Pln,
        option: Opt,
        label: `${curr.SchDesc1} - ${curr.PlnDesc} - ${curr.OptDesc}`,
        isMultiBroker: multibrokflg === YES,
        details: curr,
        value: `${Sch}-${Pln}-${Opt}`,
      };
      return [...prev, currentOption];
    }
    return prev;
  }, []);
};
export const getStpSchemes = (
  fundDetails: InvestorFundDetails[],
  fundCode: string,
  folio: string | number
): {
  scheme: string;
  plan: string;
  option: string;
  label: string;
  isMultiBroker: boolean;
  details: InvestorFundDetails;
  value: string;
}[] => {
  return fundDetails.reduce((prev, curr) => {
    const { Acno, Sch, Pln, Opt, Fund, SchDesc, multibrokflg, BalUnits } = curr;

    const isSameFund = Fund === fundCode;
    const isSameFolio = String(folio) === String(Acno);

    const isValidScheme = [isSameFund, isSameFolio, BalUnits].every(Boolean);

    if (isValidScheme) {
      const currentOption = {
        scheme: Sch,
        plan: Pln,
        option: Opt,
        label: `${SchDesc}`,
        isMultiBroker: multibrokflg === YES,
        details: curr,
        value: `${Sch}-${Pln}-${Opt}`,
      };
      return [...prev, currentOption];
    }
    return prev;
  }, []);
};
export const FAMILY_OPTIONS = [
  {
    label: "Self",
    value: "SE",
  },
  {
    label: "Spouse",
    value: "SP",
  },
  {
    label: "Dependent Children",
    value: "DC",
  },
  {
    label: "Dependent Siblings",
    value: "DS",
  },
  {
    label: "Dependent Parents",
    value: "DP",
  },
  {
    label: "Guardian",
    value: "GD",
  },
];
